import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, Inbox } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('masterToken') || localStorage.getItem('master_token') || localStorage.getItem('auth_token') || localStorage.getItem('token')}` });

export default function MasterFormSubmissions() {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/master/forms/${formId}/submissions?limit=500`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setForm(data.form);
          setSubs(data.submissions || []);
        }
      } finally { setLoading(false); }
    })();
  }, [formId]);

  const downloadCsv = async () => {
    const res = await fetch(`${API}/master/forms/${formId}/export.csv`, { headers: authHeaders() });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form?.slug || formId}-submissions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;
  if (!form) return <div className="text-red-400">Form not found</div>;

  return (
    <div className="space-y-5" data-testid="master-form-submissions">
      <Link to="/master/forms" className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-white"><ArrowLeft size={14} /> Back to all forms</Link>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne">{form.title}</h1>
          <p className="text-[#9CA3AF] text-sm mt-1">{subs.length} submissions · /forms/{form.slug}</p>
        </div>
        <button onClick={downloadCsv} disabled={subs.length === 0} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm disabled:opacity-60" data-testid="export-csv-btn">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {subs.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-white/10">
          <Inbox size={32} className="text-[#A78BFA] mx-auto mb-3" />
          <p className="text-white font-semibold">No responses yet</p>
          <p className="text-[#9CA3AF] text-sm">Share your form and they'll show up here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-3 py-2 text-left text-[#9CA3AF] font-semibold">Submitted</th>
                    {form.fields.slice(0, 2).map((f) => <th key={f.id} className="px-3 py-2 text-left text-[#9CA3AF] font-semibold">{f.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.submission_id} onClick={() => setActive(s)} className={`cursor-pointer border-t border-white/5 hover:bg-white/5 ${active?.submission_id === s.submission_id ? 'bg-purple-500/10' : ''}`} data-testid={`sub-row-${s.submission_id}`}>
                      <td className="px-3 py-2 text-[#9CA3AF] whitespace-nowrap">{new Date(s.submitted_at).toLocaleString()}</td>
                      {form.fields.slice(0, 2).map((f) => (
                        <td key={f.id} className="px-3 py-2 text-white truncate max-w-[200px]">
                          {Array.isArray(s.data?.[f.id]) ? s.data[f.id].join(', ') : (s.data?.[f.id] || '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <h3 className="font-syne text-white font-bold mb-3">Detail</h3>
            {!active ? (
              <p className="text-sm text-[#9CA3AF]">Pick a row on the left to see the full submission.</p>
            ) : (
              <div className="space-y-3 text-sm" data-testid="sub-detail">
                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Submitted at</p>
                <p className="text-white">{new Date(active.submitted_at).toLocaleString()}</p>
                <hr className="border-white/10" />
                {form.fields.map((f) => {
                  const v = active.data?.[f.id];
                  const display = Array.isArray(v) ? v.join(', ') : (v || '—');
                  return (
                    <div key={f.id}>
                      <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">{f.label}</p>
                      <p className="text-white whitespace-pre-wrap">{display}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
